import { Logger, useIntl, useIsMounted } from '@capsicum/common';
import {
	Badge,
	BaseSearchInput,
	CardCarousel,
	ChipList,
	DrawerWrapper,
	ModalService,
	Text,
	useRouter,
	useRTL
} from '@capsicum/common-web';
import { PartnerFilterIcon } from '@capsicum/common-web/icons';
import { useRPAuctions } from '@capsicum/features/fanshop/shared';
import { ChipsSkeletons, EmptyRewardsGoat, PartnerFilter, RPAuctionCard } from '@capsicum/features/fanshop/web';
import { HeaderWarning } from '@capsicum/features/regulations/web';
import { useCallback, useRef, useState } from 'react';
import styled, { useTheme } from 'styled-components';

import { useFetchNextProducts, useRPAuctionsTutorial } from '../../hooks';
import { LoadingIndicator } from '../../ui/loading-indicator/LoadingIndicator';

/**
 * RPAuctions component
 * @param {Array<string>} forcedPartnerIds - Array of partner ids to filter the auctions
 * @param {boolean} withFilter - Whether to explicitly show the filter section to avoid relaying only on the forcedPartnerIds checks as this could be used to pre-filter the auctions
 * @param {boolean} withMargin - Whether to add margin to the component to be able to use this component inside a container with margin
 * @returns {JSX.Element} RPAuctions component
 */
export const RPAuctions = ({
	forcedPartnerIds = [],
	withFilter = true,
	withMargin = true
}: {
	forcedPartnerIds?: Array<string>;
	withFilter?: boolean;
	withMargin?: boolean;
}) => {
	const { breakpoints } = useTheme();
	const { $t } = useIntl();
	const { isRTL } = useRTL();
	const { push } = useRouter();
	const [drawerState, setDrawerState] = useState(false);
	const listEndRef = useRef<HTMLDivElement>(null);

	const isMounted = useIsMounted();

	const {
		auctions,
		chipsProductTypes,
		fetchNextPage,
		handleSearch,
		hasNextPage,
		isFetched,
		isFetching,
		isFetchingNextPage,
		isLoading,
		isLoadingProductTypes,
		selectedPartners,
		selectProductType,
		userAuctions
	} = useRPAuctions({
		forcedPartnerIds,
		isWebSocketDismissed: !isMounted()
	});

	useRPAuctionsTutorial(isLoading);

	const loadPartnerFilter = useCallback(
		(showDrawer: boolean) => {
			if (window.innerWidth < breakpoints.points.mobile.maxScreenWidth) {
				ModalService.addModal({
					detached: false,
					fullHeight: true,
					renderComponent: ({ close }) => <PartnerFilter closeModal={close} />
				});
			} else {
				setDrawerState(showDrawer);
			}
		},
		[breakpoints.points.mobile.maxScreenWidth]
	);

	const handleFetchNextPage = useCallback(async () => {
		if (hasNextPage && !isFetchingNextPage) {
			try {
				await fetchNextPage();
			} catch (error) {
				Logger.error('Failed to load more items:', { error });
			}
		}
	}, [hasNextPage, fetchNextPage, isFetchingNextPage]);

	useFetchNextProducts({ fetchNextPage: handleFetchNextPage, hasNextPage, isFetching, listEndRef });

	return (
		<>
			<S.AuctionsHeaderWarning horizontalPadding page="rewards" verticalPadding />
			<S.PageWrapper $withMargin={withMargin}>
				{userAuctions.length > 0 && (
					<div>
						<S.MyBidsTitle variant="title5">{$t('rewards.rpAuctions.myBids')}</S.MyBidsTitle>
						<CardCarousel cardWidth={173} testID="my-rp-auctions-carousel" variant="fullWidth">
							{userAuctions.map(auction => (
								<RPAuctionCard
									key={`${auction?.id}-${auction?.label}`}
									testID={`rp-auctions-myBid-${auction.id}`}
									variant={auction}
									onClick={() => push(`/fan-shop/rewards/auction/${auction?.auctionId}`)}
								/>
							))}
						</CardCarousel>
					</div>
				)}
				{withFilter && (
					<>
						<S.FilterWrapper>
							<S.SearchInputWrapper
								debounceTime={500}
								name="rp-auctions-search-input"
								placeholder={$t('rewards.rpAuctions.searchBar.placeholder')}
								testID="rp-auctions-search-input"
								onChange={e => handleSearch(e.target.value)}
								onClear={() => handleSearch('')}
							/>

							<S.BadgeWrapper>
								<Badge
									enabled={!!selectedPartners?.length}
									icon={PartnerFilterIcon}
									testID="rp-auctions-filter-button"
									variant="green"
									onClick={() => loadPartnerFilter(true)}
								/>
							</S.BadgeWrapper>
						</S.FilterWrapper>

						<DrawerWrapper
							drawerPosition="right"
							isRTL={isRTL}
							showDrawer={drawerState}
							testID="rp-auctions-partner-filter-drawer-wrapper"
							onCloseDrawer={() => {
								loadPartnerFilter(false);
							}}
						>
							<PartnerFilter
								closeModal={() => {
									loadPartnerFilter(false);
								}}
							/>
						</DrawerWrapper>
					</>
				)}
				{isLoadingProductTypes ? (
					<ChipsSkeletons items={5} testID="rp-auctions" />
				) : (
					chipsProductTypes?.length > 0 && (
						<ChipList
							hasWrapper={false}
							items={chipsProductTypes}
							itemsSelected={selectProductType}
							selectionType="multiple"
							testID="rp-auctions-product-type-selector"
						/>
					)
				)}
				<S.AuctionCardWrapper>
					{isLoading ? (
						<LoadingIndicator />
					) : (
						auctions.length > 0 &&
						auctions.map((auction, index) => (
							<RPAuctionCard
								data-tutorial={index === 0 ? 'RPAuctionsTutorial' : undefined}
								key={`${auction?.id}-${auction?.label}`}
								testID={`rp-auctions-productVariant-${auction.id}`}
								variant={auction}
								onClick={() => push(`/fan-shop/rewards/auction/${auction?.auctionId}`)}
							/>
						))
					)}
					<div ref={listEndRef}></div>
				</S.AuctionCardWrapper>
				{isFetchingNextPage && <LoadingIndicator />}
				{isFetched && !auctions.length && <EmptyRewardsGoat title="rewards.rpAuctions.noAuctions" />}
			</S.PageWrapper>
		</>
	);
};

namespace S {
	export const PageWrapper = styled.div<{ $withMargin: boolean }>`
		width: 100%;
		display: flex;
		flex-direction: column;
		gap: ${({ theme }) => theme.spacings.medium};
		${({ theme }) => theme.breakpoints.queries.only.mobile} {
			${({ $withMargin, theme }) => `padding: ${$withMargin ? theme.spacings.xxsmall : 0}`};
		}

		${({ theme }) => theme.breakpoints.queries.above.mobile} {
			${({ $withMargin, theme }) => `padding: ${$withMargin ? theme.spacings.medium : 0}`};
			padding-top: ${({ theme }) => `${theme.spacings.xlarge}`};
		}

		${({ theme }) => theme.breakpoints.queries.above.tabletSmallDesktop} {
			${({ $withMargin, theme }) => `padding: ${$withMargin ? theme.spacings.xlarge : 0}`};
			padding-top: ${({ theme }) => `${theme.spacings.xlarge}`};
		}
	`;

	export const MyBidsTitle = styled(Text)`
		color: ${({ theme }) => theme.colors.text.main.enabled};
		margin-bottom: ${({ theme }) => theme.spacings.medium};
	`;

	export const FilterWrapper = styled.div`
		display: flex;
		flex-direction: row;
		justify-content: space-between;
		align-items: center;
	`;

	export const SearchInputWrapper = styled(BaseSearchInput)`
		width: 100%;
	`;

	export const BadgeWrapper = styled.div`
		margin-top: -${({ theme }) => theme.spacings.medium};
	`;

	export const AuctionCardWrapper = styled.div`
		width: 100%;
		display: flex;
		flex-wrap: wrap;
		flex-direction: row;
		gap: ${({ theme }) => theme.spacings.xsmall};

		${({ theme }) => theme.breakpoints.queries.below.mobile} {
			justify-content: center;
		}
	`;

	export const AuctionsHeaderWarning = styled(HeaderWarning)`
		padding-top: ${({ theme }) => theme.spacings.small};
		top: 125px;
	`;
}
